import React, { ReactNode, useCallback, useContext, useMemo } from 'react';
import LeadsContext from '../../providers/Leads/Leads.context';
import classNames from 'classnames';
import { SORTING_VALUES, CUSTOM_COL_SIZES, LeadsTableColumnsKey } from './data';
import { BODY_CONTENT_GENERATE } from './components/LeadsTableCell';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import ReorderResizeTable from 'components/ReorderResizeTable';
import { Lead } from 'models/Lead';
import ShowInactiveLeads from '../ShowInactiveLeads';
import {
  INACTIVE_PAGE,
  ONGOING_PAGE,
} from '../../providers/Leads/Leads.provider';
import { useTranslation } from 'react-i18next';
import DealContext from '../../providers/Deal/Deal.context';
import EditColumnsVisibilityButton from 'components/EditColumnsVisibilityButton';
import { SIZE } from 'models/Size';
import Counter, {
  COUNTER_VARIANTS,
} from 'components/Counter/Counter.component';
import useLead from '../../hooks/useLead';
import DisplayFilesByLeadPopupProvider from '../../providers/DisplayFilesByLeadPopup/DisplayFilesByLeadPopup.provider';
import { FILE_ACCESS_SETTINGS_BUTTON_ID } from 'data/discoveringFeature';

import './LeadsTableContainer.styles.scss';

type LeadsTableContainerProps = {
  className?: string;
};

const LeadsTableContainer: React.FC<LeadsTableContainerProps> = (props) => {
  const { className } = props;

  const {
    showInactiveLeads,
    totalOngoingPages,
    onGoingTableLeads,
    waitlistedTableLeads,
    totalWaitlistedPages,
    isLoadingOngoingTable,
    isLoadingWaitlistedTable,
    totalUnreadChatsCount,
    totalUnrepliedChatsCount,
    numberOfWaitlistedGridLeads,
    showFileAccessSettings,
    updateLead,
    removeLead,
    openLeadDrawer,
    openReorderFilesDrawer,
  } = useContext(LeadsContext);
  const { openManageListView } = useContext(DealContext);
  const {
    currentUser: { leadTableColumns },
    setCurrentUser,
    patchCurrentUser,
  } = useContext(CurrentUserContext);

  const classes = classNames('leads-table-container', className);

  const { t } = useTranslation();

  const { getBackgroundColor } = useLead();

  const onReorderL = useCallback(
    async (leadTableColumns: string[]) => {
      setCurrentUser((prev) => ({ ...prev, leadTableColumns }));

      patchCurrentUser({ leadTableColumns }, { skipToast: true });
    },
    [patchCurrentUser, setCurrentUser],
  );

  const tableHeaderTransformations: Record<string, ReactNode> = useMemo(
    () => ({
      chat: () => (
        <div className="lds-chat-header">
          <Counter
            count={totalUnreadChatsCount || totalUnrepliedChatsCount}
            isPositionAbsolute
            size={SIZE.SMALL}
            variant={
              totalUnreadChatsCount
                ? COUNTER_VARIANTS.ORANGE
                : COUNTER_VARIANTS.SILVER
            }
          >
            {totalUnreadChatsCount || totalUnrepliedChatsCount}
          </Counter>

          {t('chat')}
        </div>
      ),
    }),
    [totalUnreadChatsCount, totalUnrepliedChatsCount, t],
  );

  const tableContentTransformations = useMemo(
    () =>
      (leadTableColumns as LeadsTableColumnsKey[]).reduce(
        (acc, col) => ({
          ...acc,
          [col]: (lead: Lead) =>
            BODY_CONTENT_GENERATE[col]({ lead, updateLead, removeLead }),
        }),
        {},
      ),
    [leadTableColumns, removeLead, updateLead],
  );

  return (
    <DisplayFilesByLeadPopupProvider>
      <div className={classes}>
        <ReorderResizeTable
          columns={leadTableColumns as Array<keyof Lead>}
          sortingValues={SORTING_VALUES}
          isDataLoading={isLoadingOngoingTable}
          data={onGoingTableLeads}
          settingsValues={
            showFileAccessSettings
              ? ({
                  fileAccess: openReorderFilesDrawer,
                } as any)
              : []
          }
          settingsButtonId={FILE_ACCESS_SETTINGS_BUTTON_ID}
          customSizes={CUSTOM_COL_SIZES}
          showPlaceholder={false}
          tableHeaderContentTransformations={tableHeaderTransformations}
          tableContentTransformations={tableContentTransformations}
          pageParam={ONGOING_PAGE}
          totalPages={totalOngoingPages}
          getRowBackgroundColor={getBackgroundColor}
          onReorder={onReorderL}
          onRowClick={openLeadDrawer}
        />

        <EditColumnsVisibilityButton onClick={openManageListView} />

        <ShowInactiveLeads />

        {showInactiveLeads && !!numberOfWaitlistedGridLeads && (
          <>
            <ReorderResizeTable
              tableContentTransformations={tableContentTransformations}
              columns={leadTableColumns as Array<keyof Lead>}
              sortingValues={SORTING_VALUES}
              isDataLoading={isLoadingWaitlistedTable}
              data={waitlistedTableLeads}
              customSizes={CUSTOM_COL_SIZES}
              showPlaceholder={false}
              pageParam={INACTIVE_PAGE}
              totalPages={totalWaitlistedPages}
              getRowBackgroundColor={getBackgroundColor}
              onReorder={onReorderL}
              onRowClick={openLeadDrawer}
            />
          </>
        )}
      </div>
    </DisplayFilesByLeadPopupProvider>
  );
};

export default LeadsTableContainer;
